import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { CheckCircle2, Clock, Loader2, TriangleAlert } from 'lucide-react';
import { SwishQr } from '../components/donations/SwishQr';
import { getDonationStatus, formatOre, type DonationStatus } from '../lib/donations';

// Poll for the Swish payment-request validity window (~3 min): the donor must open the app, authenticate,
// and approve, which is far slower than a card redirect. A shorter budget would strand real payments.
const MAX_ATTEMPTS = 120;
const POLL_MS = 1500;

type View =
  | { kind: 'awaiting'; amountOre: number | null } // QR shown; polling
  | { kind: 'thanks'; amountOre: number; awaiting: boolean }
  | { kind: 'failed' }
  | { kind: 'pending' } // budget spent, still unconfirmed
  | { kind: 'notstarted' } // no token in the link — the payment was never created
  | { kind: 'notfound' };

/** Read the single-use payment-request token from the URL fragment (never sent to our server). */
function tokenFromHash(): string | null {
  const hash = window.location.hash.replace(/^#/, '');
  const params = new URLSearchParams(hash);
  const t = params.get('token');
  return t && t.trim() ? t.trim() : null;
}

/**
 * Spec 007b — the Swish payment page. The donor lands here from the donate form; we show a QR built from
 * the payment-request token and poll the donation status until it is confirmed at Swish. Four states:
 * awaiting (QR + loading) → thanks (paid / awaiting_posting — the gift is real) → failed ("didn't
 * complete") → pending after the budget (neutral "still confirming"). A missing token or unknown ref is a
 * specific message, never a blank page, and a thank-you is NEVER shown before the donation is `paid`.
 */
export default function DonateSwish() {
  const { ref } = useParams<{ ref: string }>();
  const [token] = useState<string | null>(tokenFromHash);
  const [view, setView] = useState<View>({ kind: 'awaiting', amountOre: null });
  const attempts = useRef(0);

  useEffect(() => {
    if (!ref) {
      setView({ kind: 'notfound' });
      return;
    }
    if (!token) {
      // The redirect arrived without a token — the Swish request was never created for this ref.
      setView({ kind: 'notstarted' });
      return;
    }
    let active = true;
    let timer: ReturnType<typeof setTimeout>;

    const terminalFor = (status: DonationStatus, amountOre: number): View | null => {
      if (status === 'paid' || status === 'awaiting_posting' || status === 'refunded') {
        return { kind: 'thanks', amountOre, awaiting: status === 'awaiting_posting' };
      }
      if (status === 'failed' || status === 'cancelled') return { kind: 'failed' };
      return null; // still pending
    };

    const poll = async () => {
      attempts.current += 1;
      const r = await getDonationStatus(ref);
      if (!active) return;
      if (r.kind === 'notfound') {
        setView({ kind: 'notfound' });
        return;
      }
      if (r.kind === 'ok') {
        const settled = terminalFor(r.data.status, r.data.amountOre);
        if (settled) {
          setView(settled);
          return;
        }
        setView({ kind: 'awaiting', amountOre: r.data.amountOre });
      }
      if (attempts.current >= MAX_ATTEMPTS) {
        setView({ kind: 'pending' });
        return;
      }
      timer = setTimeout(poll, POLL_MS);
    };

    void poll();
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [ref, token]);

  const deepLink = token ? `swish://paymentrequest?token=${encodeURIComponent(token)}` : '#';
  const amountLabel = view.kind === 'awaiting' && view.amountOre ? ` of ${formatOre(view.amountOre)}` : '';

  return (
    <div className="mx-auto w-full max-w-lg px-6 py-24 text-center" aria-live="polite">
      {view.kind === 'awaiting' && (
        <>
          <h1 className="font-display text-2xl font-semibold uppercase tracking-[0.12em] text-ink">
            Pay with Swish
          </h1>
          <p className="mx-auto mt-3 max-w-sm font-serif text-lg leading-relaxed text-muted">
            Scan this code with your phone camera or the Swish app, then approve the payment{amountLabel} to
            complete your gift.
          </p>
          <div className="mt-8">
            <SwishQr
              payload={deepLink}
              alt={`Swish payment QR code${amountLabel}. Scan it with your phone camera or the Swish app to pay.`}
            />
          </div>
          <a
            href={deepLink}
            className="mt-6 inline-flex items-center justify-center rounded-md bg-accent px-4 py-2 font-sans text-sm font-medium text-bg transition-opacity hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
          >
            Open Swish on this device
          </a>
          <p className="mt-6 flex items-center justify-center gap-2 font-sans text-xs text-muted">
            <Loader2 aria-hidden className="h-3.5 w-3.5 animate-spin" />
            Waiting for your approval — this page updates on its own.
          </p>
        </>
      )}

      {view.kind === 'thanks' && (
        <>
          <CheckCircle2 aria-hidden className="mx-auto h-10 w-10 text-accent" />
          <h1 className="mt-4 font-display text-2xl font-semibold uppercase tracking-[0.12em] text-ink">
            Thank you
          </h1>
          <p className="mt-3 font-serif text-lg leading-relaxed text-muted">
            Your gift of <strong className="text-ink">{formatOre(view.amountOre)}</strong> supports the
            order on a cost-recovery basis (§7). It appears in our open books for every member to see.
            {view.awaiting ? ' It will be booked as soon as the current financial year opens.' : ''}
          </p>
          <Link
            to="/"
            className="mt-8 inline-flex items-center justify-center rounded-md border border-border bg-surface px-4 py-2 font-sans text-sm text-ink transition-colors hover:border-accent focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
          >
            Back to the site
          </Link>
        </>
      )}

      {view.kind === 'failed' && (
        <>
          <TriangleAlert aria-hidden className="mx-auto h-10 w-10 text-error" />
          <h1 className="mt-4 font-display text-2xl font-semibold uppercase tracking-[0.12em] text-ink">
            Payment didn’t complete
          </h1>
          <p className="mt-3 font-serif text-lg leading-relaxed text-muted">
            The Swish payment was cancelled or declined, so nothing was charged. You’re welcome to try again.
          </p>
          <Link
            to="/donate"
            className="mt-8 inline-flex items-center justify-center rounded-md bg-accent px-4 py-2 font-sans text-sm font-medium text-bg transition-opacity hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
          >
            Try again
          </Link>
        </>
      )}

      {view.kind === 'pending' && (
        <>
          <Clock aria-hidden className="mx-auto h-10 w-10 text-muted" />
          <h1 className="mt-4 font-display text-2xl font-semibold uppercase tracking-[0.12em] text-ink">
            We’re confirming your gift
          </h1>
          <p className="mt-3 font-serif text-lg leading-relaxed text-muted">
            This is taking a little longer than usual. If you completed the payment in Swish, it will still
            be recorded — and if you left an email, a receipt will follow. You don’t need to do anything.
          </p>
          <Link
            to="/"
            className="mt-8 inline-flex items-center justify-center rounded-md border border-border bg-surface px-4 py-2 font-sans text-sm text-ink transition-colors hover:border-accent focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
          >
            Back to the site
          </Link>
        </>
      )}

      {view.kind === 'notstarted' && (
        <>
          <TriangleAlert aria-hidden className="mx-auto h-10 w-10 text-error" />
          <h1 className="mt-4 font-display text-2xl font-semibold uppercase tracking-[0.12em] text-ink">
            We couldn’t start the payment
          </h1>
          <p className="mt-3 font-serif text-lg leading-relaxed text-muted">
            This link is missing the details needed to show your Swish code. Nothing was charged — please
            start the donation again.
          </p>
          <Link
            to="/donate"
            className="mt-8 inline-flex items-center justify-center rounded-md bg-accent px-4 py-2 font-sans text-sm font-medium text-bg transition-opacity hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
          >
            Make a donation
          </Link>
        </>
      )}

      {view.kind === 'notfound' && (
        <>
          <TriangleAlert aria-hidden className="mx-auto h-10 w-10 text-error" />
          <h1 className="mt-4 font-display text-2xl font-semibold uppercase tracking-[0.12em] text-ink">
            We couldn’t find that donation
          </h1>
          <p className="mt-3 font-serif text-lg leading-relaxed text-muted">
            This payment link doesn’t match anything on our side. If you just donated and left an email,
            your receipt will still arrive once the payment is confirmed.
          </p>
          <Link
            to="/donate"
            className="mt-8 inline-flex items-center justify-center rounded-md border border-border bg-surface px-4 py-2 font-sans text-sm text-ink transition-colors hover:border-accent focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
          >
            Make a donation
          </Link>
        </>
      )}
    </div>
  );
}
